// Loading skeletons - animated placeholders that maintain the eventual content's footprint so
// the layout doesn't jump on data arrival.

import type { JSX } from 'preact';

function Line({
  width = '100%',
  height = '0.75rem',
  class: extraClass,
}: {
  width?: string;
  height?: string;
  class?: string;
}) {
  return (
    <div
      class={`animate-pulse rounded bg-surface-1 ${extraClass ?? ''}`}
      style={{ width, height }}
      aria-hidden="true"
    />
  );
}

function Card({ class: extraClass }: { class?: string } = {}) {
  return (
    <div class={`overflow-hidden rounded-md border border-rule bg-surface-raised ${extraClass ?? ''}`}>
      <div class="aspect-[16/9] animate-pulse bg-surface-2" />
      <div class="flex flex-col gap-3 p-4">
        <Line width="70%" height="1rem" />
        <Line width="40%" height="0.625rem" />
        <div class="mt-2 flex items-center justify-between border-t border-rule pt-3">
          <Line width="40%" height="0.625rem" />
          <Line width="20%" height="0.625rem" />
        </div>
      </div>
    </div>
  );
}

function Hero({ class: extraClass }: { class?: string } = {}) {
  return (
    <div class={`space-y-6 ${extraClass ?? ''}`}>
      <div class="aspect-[16/9] animate-pulse rounded-lg bg-surface-1" />
      <Line width="50%" height="2rem" />
      <Line height="1rem" />
      <Line width="80%" height="1rem" />
    </div>
  );
}

function Row({ class: extraClass }: { class?: string } = {}) {
  return (
    <div
      class={`h-9 animate-pulse rounded bg-surface-2 ${extraClass ?? ''}`}
      aria-hidden="true"
    />
  );
}

interface GridProps {
  count: number;
  mode?: 'card' | 'row';
  class?: string;
}

function Grid({ count, mode = 'card', class: extraClass }: GridProps): JSX.Element {
  if (mode === 'row') {
    return (
      <div
        class={`space-y-1 rounded-md border border-rule bg-surface-1 p-2 ${extraClass ?? ''}`}
        aria-hidden="true"
      >
        {Array.from({ length: count }).map((_, i) => (
          <Row key={i} />
        ))}
      </div>
    );
  }
  return (
    <div
      class={`grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 ${extraClass ?? ''}`}
      aria-hidden="true"
    >
      {Array.from({ length: count }).map((_, i) => (
        <Card key={i} />
      ))}
    </div>
  );
}

export const Skeleton = { Line, Card, Hero, Row, Grid };
